import { useCallback } from 'react';
import type { FilterTree } from '~/_shared/types/filterTree.types';
import type { MapId } from '~/_shared/types/map';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useAjaxCall } from '~/_shared/utils/hooks/useAjaxCall';
import type { MetricValue } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { OPTIMIZED_TERRITORY_DEFAULT_TARGET_TERRITORY_COUNT } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritoriesModal.container';
import {
  createOptimizedTerritories,
  type CreateOptimizedTerritoriesRequest,
  type OptimizedTerritoryAggMetric, type OptimizedTerritoryAnchorMap, type OptimizedTerritoryBtgSettings, type OptimizedTerritoryConstraintServerModel,
  type OptimizedTerritoryGlobalMetric,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/optimizedTerritories.repository';
import type {
  BoundaryGroupId, BoundaryId, BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';

type CreateOptimizedTerritoriesRequestParams = {
  mapId: MapId;
  clientId: number;
  spreadsheetId: SpreadsheetId;
  boundaryGroupId: BoundaryGroupId;
  groupName: string;

  iterations: number;
  dataMetricsValues: MetricValue[];
  demographicsValues: MetricValue[];
  globalMetrics: ReadonlyArray<OptimizedTerritoryGlobalMetric>;

  anchorPointsImportance?: number;
  anchorsMap?: OptimizedTerritoryAnchorMap;
  blockSubset?: ReadonlyArray<BoundaryId>;
  btgSettings?: OptimizedTerritoryBtgSettings;
  excludeDisconnectedBlocks?: boolean;
  exportConfig?: boolean;
  previousTerritoriesBoundaryGroupId?: BoundaryTerritoryGroupId;
  targetTerritoryCount?: number;
  filter?: { filter_tree: FilterTree };
};

export const useCreateOptimizedTerritories = () => {
  const { invokeAjax, isLoading } = useAjaxCall((clientId: number, params: CreateOptimizedTerritoriesRequest) =>
    createOptimizedTerritories(clientId, params)
  );

  const createOptimizedTerritoriesRequest = useCallback(({
    anchorPointsImportance,
    anchorsMap,
    blockSubset,
    boundaryGroupId,
    btgSettings,
    clientId,
    dataMetricsValues,
    demographicsValues,
    excludeDisconnectedBlocks,
    exportConfig,
    globalMetrics,
    iterations,
    mapId,
    previousTerritoriesBoundaryGroupId,
    spreadsheetId,
    targetTerritoryCount,
    groupName,
    filter,
  }: CreateOptimizedTerritoriesRequestParams
  ) => {
    const aggMetrics: OptimizedTerritoryAggMetric[] = dataMetricsValues.concat(demographicsValues)
      .filter(metric => metric.checked)
      .map(metric => ({
        name: 'balance',
        attribute: metric.id,
        weight: metric.weight,
      }));

    const constraints: Array<OptimizedTerritoryConstraintServerModel> = [
      ...dataMetricsValues.filter(dataMetricsValue => dataMetricsValue.isConstraint).map(dataMetricsValue => ({
        attribute: dataMetricsValue.id,
        lower_bound: dataMetricsValue.constraintLowerBound ?? dataMetricsValue.minConstraint,
        upper_bound: dataMetricsValue.constraintUpperBound ?? dataMetricsValue.maxConstraint,
      })),
      ...demographicsValues.filter(demographicsValue => demographicsValue.isConstraint).map(demographicsValue => ({
        attribute: demographicsValue.id,
        lower_bound: demographicsValue.constraintLowerBound ?? demographicsValue.minConstraint,
        upper_bound: demographicsValue.constraintUpperBound ?? demographicsValue.maxConstraint,
      })),
    ];

    return invokeAjax(clientId, {
      map_id: mapId,
      spreadsheet_id: spreadsheetId,
      boundary_group_id: boundaryGroupId,
      agg_metrics: aggMetrics,
      global_metrics: globalMetrics.map(metric => ({
        name: metric.name.toLowerCase(),
        weight: metric.weight,
      })),
      iterations,
      target_territory_count: targetTerritoryCount ?? (anchorsMap ? undefined : OPTIMIZED_TERRITORY_DEFAULT_TARGET_TERRITORY_COUNT),
      ap_importance: anchorPointsImportance,
      exclude_disconnected_blocks: excludeDisconnectedBlocks,
      block_subset: blockSubset,
      constraints,
      anchors_map: anchorsMap ? {
        map_id: anchorsMap.mapId,
        name_column: anchorsMap.nameColumnId,
      } : undefined,
      previous_territories_boundary_group_id: previousTerritoriesBoundaryGroupId,
      btg_settings: btgSettings,
      export_config: exportConfig ?? false,
      display_name: groupName,
      filter,
    });
  }, [invokeAjax]);

  return {
    isLoading,
    createOptimizedTerritoriesRequest,
  };
};
